/**
 * Admin Vehicles Dashboard - Main entry point for Vehicles module
 * Includes Scope Model controls and Role-Based Access Management
 */
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Switch } from '../../../components/ui/switch';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/dialog';
import {
  Car, Settings, Shield, Database, TrendingUp, Users,
  ChevronRight, Globe, Sparkles, Filter, AlertTriangle,
  MapPin, UserCog, Eye, Edit, FileText, Clock, Plus,
  CheckCircle, XCircle, Banknote
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminVehiclesDashboard = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [auditLogs, setAuditLogs] = useState([]);
  const [roles, setRoles] = useState([]);
  const [addRoleDialog, setAddRoleDialog] = useState(false);
  const [newRole, setNewRole] = useState({ name: '', email: '', role: 'moderator', country: 'all' });

  useEffect(() => {
    fetchOverview();
    fetchAuditLogs();
    fetchRoles();
  }, []);

  const fetchOverview = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/vehicles/config/overview`);
      setOverview(response.data.overview);
    } catch (error) {
      console.error('Failed to fetch overview:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/vehicles/audit-logs?limit=20`);
      setAuditLogs(response.data.logs || []);
    } catch (error) {
      // Default audit logs for demo
      setAuditLogs([
        { id: '1', action: 'update_ranking_weights', entity_type: 'config', admin_id: 'admin@zeilian.co', country_scope: 'SL', created_at: new Date().toISOString(), details: 'Updated relevance weight to 35%' },
        { id: '2', action: 'toggle_feature', entity_type: 'feature', admin_id: 'admin@zeilian.co', country_scope: 'global', created_at: new Date(Date.now() - 3600000).toISOString(), details: 'Enabled AI presets globally' },
        { id: '3', action: 'approve_listing', entity_type: 'moderation', admin_id: 'moderator@zeilian.co', country_scope: 'UK', created_at: new Date(Date.now() - 7200000).toISOString(), details: 'Approved listing #12345' },
        { id: '4', action: 'add_partner', entity_type: 'partner', admin_id: 'admin@zeilian.co', country_scope: 'SL', created_at: new Date(Date.now() - 14400000).toISOString(), details: 'Added insurance partner SLIC' },
        { id: '5', action: 'update_country_rules', entity_type: 'config', admin_id: 'admin@zeilian.co', country_scope: 'EU', created_at: new Date(Date.now() - 28800000).toISOString(), details: 'Enabled CO2 emissions field' },
      ]);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/vehicles/roles`);
      setRoles(response.data.roles || []);
    } catch (error) {
      // Default roles for demo
      setRoles([
        { id: '1', name: 'John Admin', email: 'admin@zeilian.co', role: 'global_admin', country: 'all', status: 'active', last_active: new Date().toISOString() },
        { id: '2', name: 'Sarah Manager', email: 'sarah@zeilian.co', role: 'country_admin', country: 'SL', status: 'active', last_active: new Date(Date.now() - 1800000).toISOString() },
        { id: '3', name: 'James Mod', email: 'james@zeilian.co', role: 'moderator', country: 'UK', status: 'active', last_active: new Date(Date.now() - 3600000).toISOString() },
        { id: '4', name: 'Emily Partner', email: 'emily@zeilian.co', role: 'partner_manager', country: 'EU', status: 'active', last_active: new Date(Date.now() - 7200000).toISOString() },
        { id: '5', name: 'David Analyst', email: 'david@zeilian.co', role: 'analyst', country: 'all', status: 'inactive', last_active: new Date(Date.now() - 86400000).toISOString() },
      ]);
    }
  };

  const menuItems = [
    {
      title: 'Configuration',
      icon: Settings,
      path: '/admin/vehicles/config',
      description: 'Vehicle types, filters, country rules',
      badge: null,
      color: 'blue'
    },
    {
      title: 'Listings Governance',
      icon: Shield,
      path: '/admin/vehicles/governance',
      description: 'Required fields, verification policies',
      badge: null,
      color: 'green'
    },
    {
      title: 'Moderation Queue',
      icon: AlertTriangle,
      path: '/admin/vehicles/moderation',
      description: 'Fraud signals, manual review',
      badge: 'Live',
      color: 'red'
    },
    {
      title: 'Data & Enrichment',
      icon: Database,
      path: '/admin/vehicles/data',
      description: 'Make/model dictionaries, auto-fill',
      badge: null,
      color: 'purple'
    },
    {
      title: 'Ranking & Search',
      icon: TrendingUp,
      path: '/admin/vehicles/ranking',
      description: 'Ranking weights, boost products',
      badge: 'AI',
      color: 'yellow'
    },
    {
      title: 'AI Presets',
      icon: Sparkles,
      path: '/admin/vehicles/presets',
      description: 'Quick filter bundles & ranking',
      badge: 'NEW',
      color: 'purple'
    },
    {
      title: 'Listing Detail Tabs',
      icon: Eye,
      path: '/admin/vehicles/listing-tabs',
      description: 'Overview, Performance, Running Costs tabs',
      badge: 'NEW',
      color: 'teal'
    },
    {
      title: 'Partners & Services',
      icon: Users,
      path: '/admin/vehicles/partners',
      description: 'Insurance, finance, garages',
      badge: null,
      color: 'indigo'
    },
    {
      title: 'Finance Module',
      icon: Banknote,
      path: '/admin/finance',
      description: 'Lenders, referrals, eligibility matching',
      badge: 'NEW',
      color: 'green'
    }
  ];

  const roleConfig = {
    global_admin: { label: 'Global Admin', color: 'bg-red-100 text-red-700', permissions: 'Full access to all features and countries' },
    country_admin: { label: 'Country Admin', color: 'bg-blue-100 text-blue-700', permissions: 'Full access within assigned country' },
    moderator: { label: 'Moderator', color: 'bg-yellow-100 text-yellow-700', permissions: 'Review queues, approve/reject listings' },
    partner_manager: { label: 'Partner Manager', color: 'bg-green-100 text-green-700', permissions: 'Manage partners and services' },
    analyst: { label: 'Analyst', color: 'bg-gray-100 text-gray-700', permissions: 'Read-only access to metrics and reports' }
  };

  const handleAddRole = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/vehicles/roles?admin_id=admin`, newRole);
      fetchRoles();
    } catch (error) {
      // Add locally for demo
      setRoles(prev => [...prev, { ...newRole, id: Date.now().toString(), status: 'active', last_active: new Date().toISOString() }]);
    }
    setAddRoleDialog(false);
    setNewRole({ name: '', email: '', role: 'moderator', country: 'all' });
  };

  // Show nested route if we're on a sub-page
  if (location.pathname !== '/admin/vehicles') {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Car className="h-8 w-8" />
              <div>
                <h1 className="text-2xl font-bold">Vehicles Module</h1>
                <p className="text-blue-100 text-sm">Manage vehicle filtering, ranking, and configuration</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge className="bg-green-400/20 text-white border-green-400/50">
                <Globe className="h-3 w-3 mr-1" />
                Multi-Country Support
              </Badge>
              <Link to="/admin/dashboard">
                <Button variant="outline" size="sm" className="bg-white/10 hover:bg-white/20 border-white/30 text-white">
                  ← Back to Admin
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview">
              <Car className="h-4 w-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="scope">
              <Globe className="h-4 w-4 mr-2" />
              Scope Model
            </TabsTrigger>
            <TabsTrigger value="roles">
              <UserCog className="h-4 w-4 mr-2" />
              Roles & Access
            </TabsTrigger>
            <TabsTrigger value="audit">
              <FileText className="h-4 w-4 mr-2" />
              Audit Log
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold text-blue-600">
                    {overview?.vehicle_types_active || 12}
                  </div>
                  <p className="text-sm text-gray-500">Vehicle Types Active</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold text-green-600">
                    {overview?.countries_configured || 3}
                  </div>
                  <p className="text-sm text-gray-500">Countries Configured</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold text-purple-600">
                    {overview?.ai_presets_active || 24}
                  </div>
                  <p className="text-sm text-gray-500">AI Presets Active</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-yellow-500" />
                    <span className="text-lg font-semibold">AI Ranking</span>
                  </div>
                  <p className="text-sm text-green-600">Enabled</p>
                </CardContent>
              </Card>
            </div>

            {/* Menu Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {menuItems.map((item) => (
                <Link key={item.path} to={item.path}>
                  <Card className="hover:shadow-md transition-shadow cursor-pointer h-full border-l-4 border-l-blue-500">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 bg-${item.color}-50 rounded-lg`}>
                            <item.icon className={`h-5 w-5 text-${item.color}-600`} />
                          </div>
                          <div>
                            <h3 className="font-semibold flex items-center gap-2">
                              {item.title}
                              {item.badge && (
                                <Badge variant="outline" className="text-xs">
                                  {item.badge}
                                </Badge>
                              )}
                            </h3>
                            <p className="text-sm text-gray-500 mt-1">{item.description}</p>
                          </div>
                        </div>
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </TabsContent>

          {/* Scope Model Tab */}
          <TabsContent value="scope" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  Scope Model - Control Hierarchy
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-6">
                  The scope model ensures UK rules don&apos;t accidentally affect Sri Lanka and vice versa. 
                  Settings cascade from Global → Country → Local, with lower levels overriding higher ones.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Global Scope */}
                  <Card className="border-2 border-gray-300 bg-gray-50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Globe className="h-5 w-5 text-gray-600" />
                        🌍 Global (Platform-Wide)
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="space-y-2">
                        <h4 className="font-medium text-sm text-gray-700">Controls:</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-3 w-3 text-green-500" />
                            Base vehicle schema
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-3 w-3 text-green-500" />
                            Shared UI components
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-3 w-3 text-green-500" />
                            Core moderation rules
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-3 w-3 text-green-500" />
                            Default ranking weights
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-3 w-3 text-green-500" />
                            Anti-abuse thresholds
                          </li>
                        </ul>
                      </div>
                      <div className="pt-2 border-t">
                        <Badge variant="outline" className="bg-gray-100">
                          Affects All Countries
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Country/Region Scope */}
                  <Card className="border-2 border-blue-300 bg-blue-50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Shield className="h-5 w-5 text-blue-600" />
                        🏳️ Country/Region
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="space-y-2">
                        <h4 className="font-medium text-sm text-blue-700">Overrides:</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-3 w-3 text-blue-500" />
                            Enable/disable fields
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-3 w-3 text-blue-500" />
                            Compliance filters (UK/EU)
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-3 w-3 text-blue-500" />
                            Unit conversion (km/miles)
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-3 w-3 text-blue-500" />
                            Currency (LKR/GBP/EUR)
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-3 w-3 text-blue-500" />
                            Tax/insurance estimation
                          </li>
                        </ul>
                      </div>
                      <div className="pt-2 border-t flex gap-1 flex-wrap">
                        <Badge className="bg-blue-100 text-blue-700">🇱🇰 SL</Badge>
                        <Badge className="bg-blue-100 text-blue-700">🇬🇧 UK</Badge>
                        <Badge className="bg-blue-100 text-blue-700">🇪🇺 EU</Badge>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Local Scope */}
                  <Card className="border-2 border-green-300 bg-green-50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <MapPin className="h-5 w-5 text-green-600" />
                        📍 Local (City/Area)
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="space-y-2">
                        <h4 className="font-medium text-sm text-green-700">Operational:</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-3 w-3 text-green-500" />
                            Radius defaults
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-3 w-3 text-green-500" />
                            Local partner availability
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-3 w-3 text-green-500" />
                            Moderation staffing queues
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-3 w-3 text-green-500" />
                            Delivery zone rules
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-3 w-3 text-green-500" />
                            Regional promotions
                          </li>
                        </ul>
                      </div>
                      <div className="pt-2 border-t">
                        <Badge className="bg-green-100 text-green-700">
                          Fine-Grained Control
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Active Country Settings */}
                <div className="mt-8">
                  <h3 className="font-semibold mb-4">Active Country Configurations</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card>
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-2xl">🇱🇰</span>
                          <Badge className="bg-green-100 text-green-700">Active</Badge>
                        </div>
                        <h4 className="font-semibold">Sri Lanka</h4>
                        <div className="mt-2 space-y-1 text-sm text-gray-600">
                          <p>Currency: LKR (Rs.)</p>
                          <p>Distance: Kilometers</p>
                          <p>Special: Three-wheelers, Permit types</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-2xl">🇬🇧</span>
                          <Badge className="bg-green-100 text-green-700">Active</Badge>
                        </div>
                        <h4 className="font-semibold">United Kingdom</h4>
                        <div className="mt-2 space-y-1 text-sm text-gray-600">
                          <p>Currency: GBP (£)</p>
                          <p>Distance: Miles</p>
                          <p>Special: MOT, Road tax, Write-off categories</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-2xl">🇪🇺</span>
                          <Badge className="bg-green-100 text-green-700">Active</Badge>
                        </div>
                        <h4 className="font-semibold">European Union</h4>
                        <div className="mt-2 space-y-1 text-sm text-gray-600">
                          <p>Currency: EUR (€)</p>
                          <p>Distance: Kilometers</p>
                          <p>Special: CO2 emissions, WLTP compliance</p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Roles & Access Tab */}
          <TabsContent value="roles" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <UserCog className="h-5 w-5" />
                    Role-Based Access Control (RBAC)
                  </CardTitle>
                  <Button onClick={() => setAddRoleDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add User
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Role Definitions */}
                <div className="mb-6">
                  <h3 className="font-semibold mb-3">Role Definitions</h3>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                    {Object.entries(roleConfig).map(([key, config]) => (
                      <Card key={key} className="border">
                        <CardContent className="p-3">
                          <Badge className={`${config.color} mb-2`}>{config.label}</Badge>
                          <p className="text-xs text-gray-600">{config.permissions}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                {/* User Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left py-3 px-4">User</th>
                        <th className="text-left py-3 px-4">Role</th>
                        <th className="text-left py-3 px-4">Country Scope</th>
                        <th className="text-left py-3 px-4">Status</th>
                        <th className="text-left py-3 px-4">Last Active</th>
                        <th className="text-right py-3 px-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {roles.map((user) => (
                        <tr key={user.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <div>
                              <p className="font-medium">{user.name}</p>
                              <p className="text-sm text-gray-500">{user.email}</p>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <Badge className={roleConfig[user.role]?.color || 'bg-gray-100'}>
                              {roleConfig[user.role]?.label || user.role}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            {user.country === 'all' ? (
                              <Badge variant="outline">🌍 All Countries</Badge>
                            ) : (
                              <Badge variant="outline">
                                {user.country === 'SL' ? '🇱🇰' : user.country === 'UK' ? '🇬🇧' : '🇪🇺'} {user.country}
                              </Badge>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <Badge variant={user.status === 'active' ? 'default' : 'secondary'}>
                              {user.status}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-500">
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {new Date(user.last_active).toLocaleString()}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <div className="flex gap-2 justify-end">
                              <Button variant="outline" size="sm">
                                <Edit className="h-3 w-3" />
                              </Button>
                              <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700">
                                <XCircle className="h-3 w-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Audit Log Tab */}
          <TabsContent value="audit" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Audit Log - Admin Actions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Every admin action creates an audit log entry tracking who, what, when, and country scope.
                </p>
                <div className="space-y-3">
                  {auditLogs.map((log) => (
                    <div key={log.id} className="flex items-start gap-4 p-4 border rounded-lg hover:bg-gray-50">
                      <div className={`p-2 rounded-full ${
                        log.action.includes('approve') ? 'bg-green-100' :
                        log.action.includes('reject') ? 'bg-red-100' :
                        log.action.includes('update') ? 'bg-blue-100' :
                        'bg-gray-100'
                      }`}>
                        {log.action.includes('approve') ? <CheckCircle className="h-4 w-4 text-green-600" /> :
                         log.action.includes('reject') ? <XCircle className="h-4 w-4 text-red-600" /> :
                         log.action.includes('update') ? <Edit className="h-4 w-4 text-blue-600" /> :
                         <Settings className="h-4 w-4 text-gray-600" />}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium capitalize">{log.action.replace(/_/g, ' ')}</span>
                          <Badge variant="outline" className="text-xs">{log.entity_type}</Badge>
                          <Badge variant="outline" className="text-xs">
                            {log.country_scope === 'global' ? '🌍' : 
                             log.country_scope === 'SL' ? '🇱🇰' : 
                             log.country_scope === 'UK' ? '🇬🇧' : '🇪🇺'} {log.country_scope}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600">{log.details}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                          <span>By: {log.admin_id}</span>
                          <span>•</span>
                          <span>{new Date(log.created_at).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Role Dialog */}
      <Dialog open={addRoleDialog} onOpenChange={setAddRoleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Admin User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="mb-2 block">Full Name</Label>
              <Input
                value={newRole.name}
                onChange={(e) => setNewRole(p => ({ ...p, name: e.target.value }))}
                placeholder="John Doe"
              />
            </div>
            <div>
              <Label className="mb-2 block">Email</Label>
              <Input
                type="email"
                value={newRole.email}
                onChange={(e) => setNewRole(p => ({ ...p, email: e.target.value }))}
                placeholder="john@zeilian.co"
              />
            </div>
            <div>
              <Label className="mb-2 block">Role</Label>
              <Select value={newRole.role} onValueChange={(v) => setNewRole(p => ({ ...p, role: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="global_admin">Global Admin</SelectItem>
                  <SelectItem value="country_admin">Country Admin</SelectItem>
                  <SelectItem value="moderator">Moderator</SelectItem>
                  <SelectItem value="partner_manager">Partner Manager</SelectItem>
                  <SelectItem value="analyst">Analyst</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-2 block">Country Scope</Label>
              <Select value={newRole.country} onValueChange={(v) => setNewRole(p => ({ ...p, country: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">🌍 All Countries</SelectItem>
                  <SelectItem value="SL">🇱🇰 Sri Lanka</SelectItem>
                  <SelectItem value="UK">🇬🇧 United Kingdom</SelectItem>
                  <SelectItem value="EU">🇪🇺 European Union</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddRoleDialog(false)}>Cancel</Button>
            <Button onClick={handleAddRole} disabled={!newRole.name || !newRole.email}>
              <Plus className="h-4 w-4 mr-2" />
              Add User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminVehiclesDashboard;
